import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Clock, X, ArrowRight } from "lucide-react";
import { API } from "../lib/api";
import axios from "axios";

const STATUS_COPY = {
  paid: {
    title: "BEDANKT!",
    accent: "PROOST.",
    color: "var(--tha-red)",
    icon: Check,
    body: "Je betaling is gelukt. We sturen 'm zo snel mogelijk jouw kant op. Check je inbox voor de bevestiging.",
  },
  awaiting_payment: {
    title: "EVEN WACHTEN…",
    accent: "WE VERWERKEN.",
    color: "#facc15",
    icon: Clock,
    body: "Je betaling wordt nog verwerkt. Dit duurt meestal hooguit een paar seconden. Deze pagina ververst automatisch.",
  },
  failed: {
    title: "OEPS.",
    accent: "BETALING MISLUKT.",
    color: "var(--tha-red)",
    icon: X,
    body: "De betaling is niet gelukt. Geen geld afgeschreven. Probeer het opnieuw via de shop.",
  },
  cancelled: {
    title: "GEANNULEERD.",
    accent: "GEEN PROBLEEM.",
    color: "#9ca3af",
    icon: X,
    body: "Je hebt de betaling afgebroken. Geen geld afgeschreven. Wanneer je wilt kun je het gewoon opnieuw proberen.",
  },
  expired: {
    title: "TE LAAT.",
    accent: "BETALING VERLOPEN.",
    color: "#9ca3af",
    icon: Clock,
    body: "De betalingssessie is verlopen. Plaats je bestelling opnieuw via de shop.",
  },
};

const CheckoutResult = () => {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const fetchStatus = useCallback(async () => {
    if (!orderId) {
      setError("Geen bestelling-ID gevonden in de URL.");
      return;
    }
    try {
      const res = await axios.get(`${API}/orders/${orderId}/status`);
      setOrder(res.data);
    } catch (e) {
      if (e?.response?.status === 404) setError("Bestelling niet gevonden.");
      else setError("Kon status niet ophalen.");
    }
  }, [orderId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Poll if still awaiting payment, max 20 attempts (~60s)
  useEffect(() => {
    if (!order) return undefined;
    if (order.payment_status !== "awaiting_payment") return undefined;
    if (attempts >= 20) return undefined;
    const id = setTimeout(() => {
      setAttempts((a) => a + 1);
      fetchStatus();
    }, 3000);
    return () => clearTimeout(id);
  }, [order, attempts, fetchStatus]);

  if (error) {
    return (
      <div data-testid="checkout-result-error" className="py-24">
        <div className="mx-auto max-w-2xl px-5 sm:px-8 text-center">
          <h1 className="font-display text-5xl">
            FOUT. <span className="neon-red-text">{error.toUpperCase()}</span>
          </h1>
          <Link
            to="/shop"
            className="mt-8 inline-flex items-center gap-2 btn-red px-6 py-3 font-display tracking-wider text-sm"
          >
            TERUG NAAR SHOP <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="py-24 text-center text-[var(--tha-muted)]">Status ophalen…</div>
    );
  }

  const copy = STATUS_COPY[order.payment_status] || STATUS_COPY.failed;
  const Icon = copy.icon;

  return (
    <div data-testid={`checkout-result-${order.payment_status}`} className="py-24">
      <div className="mx-auto max-w-2xl px-5 sm:px-8 text-center">
        <div
          className="inline-flex items-center justify-center w-20 h-20 border-2 rounded-full mb-8"
          style={{ borderColor: copy.color, color: copy.color }}
        >
          <Icon size={28} />
        </div>
        <h1 className="font-display text-5xl sm:text-7xl leading-none">
          {copy.title}{" "}
          <span style={{ color: copy.color }} className={copy.color === "var(--tha-red)" ? "neon-red-text" : ""}>
            {copy.accent}
          </span>
        </h1>
        <p className="mt-6 text-[var(--tha-muted)] max-w-md mx-auto">{copy.body}</p>

        <div className="mt-10 border border-[var(--tha-border)] p-6 text-left max-w-md mx-auto">
          <div className="text-xs font-mono-x tracking-widest text-[var(--tha-muted)]">BESTELLING</div>
          <div className="mt-3 flex justify-between text-sm">
            <span className="text-[var(--tha-muted)]">Nummer</span>
            <span className="font-mono-x text-white">#{order.id.slice(0, 8).toUpperCase()}</span>
          </div>
          <div className="mt-2 flex justify-between text-sm">
            <span className="text-[var(--tha-muted)]">Totaal</span>
            <span className="text-white">€ {order.total.toFixed(2)}</span>
          </div>
          <div className="mt-2 flex justify-between text-sm">
            <span className="text-[var(--tha-muted)]">Status</span>
            <span style={{ color: copy.color }} className="font-mono-x tracking-widest">
              {order.payment_status.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link
            to="/"
            className="btn-ghost-red px-6 py-3 font-display tracking-wider text-sm"
          >
            TERUG NAAR HOME
          </Link>
          {(order.payment_status === "failed" ||
            order.payment_status === "cancelled" ||
            order.payment_status === "expired") && (
            <Link
              to="/shop"
              className="btn-red px-6 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2"
            >
              OPNIEUW PROBEREN <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutResult;
